import { useEffect } from 'react';
import { ACTION_TYPES, useHomeDispatch, useHomeState } from './home-context';
import HomeAppService from '../../../../application/platform/apm/home/home-app-service';
import { TipoSecaoHome } from '../../../../model/apm/apm-enums';
import { IHomeSectionResponse } from '../../../../model/apm/home/response/home-section-response';
import { IHomeSectionItemResponse } from '../../../../model/apm/home/response/home-section-item-response';
import { IHomeSectionItemAddEditRequest } from '../../../../model/apm/home/request/home-section-item-add-edit-request';
import { reorderOptimistic } from '../../components/reorder-utils';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { removeById, upsertById } from '../../../../utils/list-utils';
import { buildHomeItemRequest, buildHomeSectionRequest } from '../components/home-request-builders';

export const useHomeController = () => {
    const dispatch = useHomeDispatch();
    const state = useHomeState();
    const { isSaving, runSaving, run, showSuccess, confirmDelete } = useActionRunner();

    async function loadHome() {
        dispatch({ type: ACTION_TYPES.setLoading, payload: true });
        await run('Não foi possível carregar a configuração da home', async () => {
            dispatch({ type: ACTION_TYPES.setHome, payload: await HomeAppService.get() });
        });
        dispatch({ type: ACTION_TYPES.setLoading, payload: false });
    }

    useEffect(() => {
        loadHome();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleToggleStatus() {
        if (!state.home) return;
        const isEnabled = !state.home.isEnabled;
        runSaving('Não foi possível alterar o status da home', async () => {
            dispatch({ type: ACTION_TYPES.setHome, payload: await HomeAppService.changeStatus(isEnabled) });
            showSuccess(isEnabled ? 'Home customizada ativada!' : 'Home customizada desativada. O portal volta a usar a home padrão.');
        });
    }

    // Seções
    function setSections(sections: IHomeSectionResponse[]) {
        dispatch({ type: ACTION_TYPES.setSections, payload: sections });
    }

    function replaceSection(saved: IHomeSectionResponse) {
        setSections(state.sections.map((s) => (s.id === saved.id ? { ...saved, items: saved.items ?? s.items } : s)));
    }

    function openSectionDrawer(section?: IHomeSectionResponse) {
        dispatch({ type: ACTION_TYPES.openSectionDrawer, payload: section });
    }

    function closeSectionDrawer() {
        dispatch({ type: ACTION_TYPES.closeSectionDrawer });
    }

    function handleSaveSection(value: Record<string, any>) {
        const editing = state.sectionDrawer.section;
        const type: TipoSecaoHome = editing ? editing.type : value.type;
        const request = buildHomeSectionRequest(type, value);
        return runSaving('Não foi possível salvar a seção', async () => {
            if (editing) {
                replaceSection(await HomeAppService.editSection(editing.id, request));
            } else {
                await HomeAppService.addSection({ ...request, type });
                // Na primeira seção a API também cria as seções fixas: recarrega para obter os ids
                await loadHome();
            }
            showSuccess(editing ? 'Seção atualizada com sucesso!' : 'Seção adicionada com sucesso!');
            closeSectionDrawer();
        });
    }

    function handleToggleSectionVisibility(section: IHomeSectionResponse) {
        run('Não foi possível alterar a visibilidade da seção', async () => {
            replaceSection(await HomeAppService.editSection(section.id, buildHomeSectionRequest(section.type, { ...section, isVisible: !section.isVisible })));
            showSuccess(section.isVisible ? 'Seção ocultada no portal.' : 'Seção exibida no portal.');
        });
    }

    function handleDeleteSection(section: IHomeSectionResponse) {
        confirmDelete('Tem certeza que deseja excluir esta seção e todos os seus itens?', () => run('Não foi possível excluir a seção', async () => {
            await HomeAppService.removeSection(section.id);
            setSections(removeById(state.sections, section.id));
            showSuccess('Seção excluída com sucesso!');
        }));
    }

    function handleReorderSections(next: IHomeSectionResponse[]) {
        run('Não foi possível reordenar as seções', () => reorderOptimistic(state.sections, next, (s) => s.id, setSections, HomeAppService.reorderSections));
    }

    // Itens
    function openItemDrawer(section: IHomeSectionResponse, item?: IHomeSectionItemResponse) {
        dispatch({ type: ACTION_TYPES.openItemDrawer, payload: { section, item } });
    }

    function closeItemDrawer() {
        dispatch({ type: ACTION_TYPES.closeItemDrawer });
    }

    function sectionItems(sectionId: number) {
        return state.sections.find((s) => s.id === sectionId)?.items || [];
    }

    function setSectionItems(sectionId: number, items: IHomeSectionItemResponse[]) {
        dispatch({ type: ACTION_TYPES.setSectionItems, payload: { sectionId, items } });
    }

    async function saveItem(section: IHomeSectionResponse, request: IHomeSectionItemAddEditRequest, editing?: IHomeSectionItemResponse) {
        const saved = await (editing ? HomeAppService.editItem(section.id, editing.id, request) : HomeAppService.addItem(section.id, request));
        setSectionItems(section.id, upsertById(sectionItems(section.id), saved));
    }

    function handleSaveItem(value: Record<string, any>) {
        const { section, item: editing } = state.itemDrawer;
        if (!section) return;
        return runSaving('Não foi possível salvar o item', async () => {
            await saveItem(section, buildHomeItemRequest(section.type, value), editing);
            showSuccess(editing ? 'Item atualizado com sucesso!' : 'Item adicionado com sucesso!');
            closeItemDrawer();
        });
    }

    function handleToggleItemVisibility(section: IHomeSectionResponse, item: IHomeSectionItemResponse) {
        run('Não foi possível alterar a visibilidade do item', async () => {
            await saveItem(section, buildHomeItemRequest(section.type, { ...item, isVisible: !item.isVisible }), item);
            showSuccess(item.isVisible ? 'Item ocultado no portal.' : 'Item exibido no portal.');
        });
    }

    function handleDeleteItem(section: IHomeSectionResponse, item: IHomeSectionItemResponse) {
        confirmDelete('Tem certeza que deseja excluir este item?', () => run('Não foi possível excluir o item', async () => {
            await HomeAppService.removeItem(section.id, item.id);
            setSectionItems(section.id, removeById(sectionItems(section.id), item.id));
            showSuccess('Item excluído com sucesso!');
        }));
    }

    function handleReorderItems(section: IHomeSectionResponse, next: IHomeSectionItemResponse[]) {
        run('Não foi possível reordenar os itens', () => reorderOptimistic(
            section.items || [], next, (i) => i.id,
            (items) => setSectionItems(section.id, items),
            (ids) => HomeAppService.reorderItems(section.id, ids),
        ));
    }

    return {
        isSaving, handleToggleStatus,
        openSectionDrawer, closeSectionDrawer, handleSaveSection, handleToggleSectionVisibility, handleDeleteSection, handleReorderSections,
        openItemDrawer, closeItemDrawer, handleSaveItem, handleToggleItemVisibility, handleDeleteItem, handleReorderItems,
    };
};
